import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlassCard, CustomInput, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { signUpWithPhone, signInWithPhone, sendPasswordResetEmail } from '../../services/authService';
import './LoginScreen.css';

// Simple Captcha Generator
const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid ambiguous chars
    let captcha = '';
    for (let i = 0; i < 6; i++) {
        captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return captcha;
};

export const LoginScreen = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const alert = useAlert();
    const [isSignUp, setIsSignUp] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    // Form fields
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [captcha, setCaptcha] = useState('');
    const [userCaptcha, setUserCaptcha] = useState('');
    const [resetPhone, setResetPhone] = useState('');
    const [isReferralFromUrl, setIsReferralFromUrl] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Initialize captcha
    useEffect(() => {
        setCaptcha(generateCaptcha());
    }, []);

    // Check for referral code in URL
    useEffect(() => {
        const refCode = searchParams.get('ref') || searchParams.get('referral');
        if (refCode) {
            setReferralCode(refCode.toUpperCase());
            setIsReferralFromUrl(true);
            setIsSignUp(true);
        }
    }, [searchParams]);

    const refreshCaptcha = () => {
        setCaptcha(generateCaptcha());
        setUserCaptcha('');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!phone || phone.length < 10) {
            newErrors.phone = 'Enter a valid 10-digit mobile number';
        }

        if (!password || password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (isSignUp) {
            if (password !== confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        if (userCaptcha.toUpperCase() !== captcha) {
            newErrors.captcha = 'Invalid captcha code';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            if (isSignUp) {
                await signUpWithPhone(phone, password, referralCode);
                alert.showSuccess('Success', 'Account created successfully!');
            } else {
                await signInWithPhone(phone, password);
            }
            navigate('/');
        } catch (error) {
            console.error('Auth error:', error);
            let msg = 'Authentication failed. Please check your credentials.';
            if (error.message && error.message.includes('blocked')) {
                msg = 'Your account has been blocked. Please contact support.';
            } else if (error.message && error.message.includes('already registered')) {
                msg = 'Mobile number already registered. Please login instead.';
            } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                msg = 'Invalid mobile number or password';
            } else if (error.code === 'auth/email-already-in-use') {
                msg = 'Mobile number already registered';
            } else if (error.message && error.message.includes('Invalid referral code')) {
                msg = 'Invalid referral code. Please check and try again.';
            }
            alert.showError('Error', msg);
            refreshCaptcha();
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!resetPhone || resetPhone.length < 10) {
            alert.showError('Error', 'Please enter your registered mobile number');
            return;
        }

        alert.showInfo('Info', 'Password reset for mobile-based accounts must be handled by support.');
        // In a real app we would use SMS OTP here
    };

    return (
        <div className="login-screen">
            <LoadingOverlay visible={loading} message={isSignUp ? 'Creating account...' : 'Signing in...'} />

            <div className="login-container">
                {/* Header Section */}
                <div className="login-header">
                    <div className="logo-section">
                        <img src="/assets/images/logo.png" alt="Yonex AI Logo" className="auth-logo" />
                    </div>
                    <h1 className="mode-title">{isSignUp ? 'Create an Account' : 'Login'}</h1>
                    <p className="mode-subtitle">
                        {isSignUp ? 'Join the platform to start earning' : 'Enter your mobile number to access your account'}
                    </p>
                </div>

                <GlassCard className="auth-card">
                    <form onSubmit={handleSubmit}>
                        {/* Mobile Number Field */}
                        <div className="input-field-group">
                            <label className="field-label">Phone Number</label>
                            <CustomInput
                                type="tel"
                                placeholder="Enter 10-digit mobile number"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                error={errors.phone}
                            />
                        </div>

                        {/* Password Field */}
                        <div className="input-field-group">
                            <label className="field-label">Password</label>
                            <CustomInput
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                isPassword
                                error={errors.password}
                            />
                        </div>

                        {isSignUp && (
                            <>
                                {/* Confirm Password */}
                                <div className="input-field-group">
                                    <label className="field-label">Confirm Password</label>
                                    <CustomInput
                                        placeholder="Retype your password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        isPassword
                                        error={errors.confirmPassword}
                                    />
                                </div>

                                {/* Invitation Code */}
                                <div className="input-field-group">
                                    <label className="field-label">Invitation Code</label>
                                    <CustomInput
                                        placeholder="Enter your friend's code"
                                        value={referralCode}
                                        onChange={(e) => !isReferralFromUrl && setReferralCode(e.target.value.toUpperCase())}
                                        disabled={isReferralFromUrl}
                                    />
                                </div>
                            </>
                        )}

                        {!isSignUp && (
                            <button
                                type="button"
                                className="forgot-link"
                                onClick={() => setShowForgotPassword(true)}
                            >
                                Forgot Password?
                            </button>
                        )}

                        {/* Captcha Section */}
                        <div className="input-field-group">
                            <label className="field-label">Captcha Code</label>
                            <div className="captcha-display-row">
                                <div className="captcha-display-box">{captcha}</div>
                                <button type="button" className="refresh-captcha" onClick={refreshCaptcha}>
                                    <span className="material-icons">refresh</span>
                                </button>
                            </div>
                            <CustomInput
                                placeholder="Enter captcha code above"
                                value={userCaptcha}
                                onChange={(e) => setUserCaptcha(e.target.value)}
                                error={errors.captcha}
                            />
                        </div>

                        <button type="submit" className="submit-auth-btn" disabled={loading}>
                            {isSignUp ? 'Register' : 'Login'}
                        </button>
                    </form>

                    <p className="switch-mode-text">
                        {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                        <button
                            type="button"
                            className="switch-mode-btn"
                            onClick={() => {
                                setIsSignUp(!isSignUp);
                                setErrors({});
                                refreshCaptcha();
                            }}
                        >
                            {isSignUp ? 'Login' : 'Register'}
                        </button>
                    </p>
                </GlassCard>
            </div>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h3 className="modal-title">Reset Password</h3>
                        <p className="modal-subtitle">
                            Please contact customer support to reset your password for mobile-authenticated accounts.
                        </p>
                        <CustomInput
                            type="tel"
                            placeholder="Enter mobile number"
                            value={resetPhone}
                            onChange={(e) => setResetPhone(e.target.value)}
                        />
                        <div className="modal-buttons">
                            <GradientButton
                                title="Close"
                                onClick={() => setShowForgotPassword(false)}
                                style={{ background: '#666' }}
                            />
                            <GradientButton
                                title="Contact Support"
                                onClick={() => navigate('/support')}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginScreen;
